import { useEffect, useState } from "react";
import { CheckCircle2, IndianRupee } from "lucide-react";
import { HomeworkShell, useHwAccount } from "./HomeworkShell";
import { hwApi, hwErr, fmtDate, inr, loadRazorpay } from "./hwApi";

function PaymentsInner() {
  const account = useHwAccount();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    hwApi.get("/store/status").then(({ data: d }) => setData(d)).catch(() => setError("Could not load payment info."));
    hwApi.get("/store/history").then(({ data: d }) => setHistory(d.orders)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const buy = async () => {
    setError("");
    setBusy(true);
    try {
      const { data: order } = await hwApi.post("/store/order");
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment window. Check your connection.");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR", name: "Pranjal Sir Physics",
        description: order.item_name, order_id: order.rzp_order_id,
        prefill: { name: order.student.name, email: order.student.email, contact: order.student.mobile },
        theme: { color: "#1d4ed8" },
        handler: async (resp) => {
          try {
            await hwApi.post("/store/verify", {
              order_id: order.order_id, razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
            setNotice("Payment successful — your Homework Program is active!");
            load();
          } catch (err) { setError(hwErr(err)); }
        },
      });
      rzp.open();
    } catch (err) { setError(hwErr(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;
  const ent = data.entitlement;
  const product = data.product;

  return (
    <div className="tp-wrap" style={{ maxWidth: 680 }} data-testid="hw-payments">
      <div className="tp-eyebrow">PAYMENTS</div>
      <h1 className="tp-h2" style={{ fontSize: 30 }}>Mentor Program</h1>
      <p className="tp-muted" style={{ marginBottom: 4 }}>NOT AI. ONLY PRANJAL SIR. Every homework is checked by hand, question by question.</p>
      {error && <p className="tp-error" role="alert" data-testid="hw-pay-error">{error}</p>}
      {notice && <p className="tp-pill ok" data-testid="hw-pay-notice">{notice}</p>}

      {ent?.active ? (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px solid #12b76a" }} data-testid="hw-pay-active">
          <b style={{ color: "#15803d" }}><CheckCircle2 size={16} /> Program active</b>
          <p className="tp-muted" style={{ margin: "8px 0 0" }}>{ent.quota_left} of {ent.quota_total} assignments remaining · valid till {fmtDate(ent.valid_until)}</p>
        </div>
      ) : data.require_subscription === false ? (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px solid #1d4ed8" }} data-testid="hw-pay-testing">
          <b style={{ color: "#1d4ed8" }}>Testing mode is on</b>
          <p className="tp-muted" style={{ margin: "8px 0 0" }}>Pranjal Sir has paused the payment requirement while testing — homework can be submitted without a subscription.</p>
        </div>
      ) : product && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hw-pay-card">
          <div className="admin-row">
            <b style={{ color: "#0b2545", flex: 1, fontSize: 17 }}>{product.name}</b>
            <b style={{ fontSize: 24, color: "#fbbf24" }}>{inr(product.price_paise)}</b>
          </div>
          <p className="tp-muted" style={{ margin: "8px 0 4px" }}>{product.description}</p>
          <p className="tp-note" style={{ margin: "0 0 12px" }}>Includes: {data.program.access_days} days access · {data.program.quota} homework assignments · {data.program.questions_per_assignment} questions each · personal evaluation &amp; feedback</p>
          {data.payments_live ? (
            <button className="tp-btn" style={{ width: "100%" }} disabled={busy} onClick={buy} data-testid="hw-buy-btn">
              <IndianRupee size={16} /> {busy ? "Preparing payment…" : `PAY ${inr(product.price_paise)} & ACTIVATE`}
            </button>
          ) : (
            <p className="tp-note" style={{ color: "#fbbf24" }} data-testid="hw-pay-pending">Online payments are being activated. WhatsApp us to join meanwhile.</p>
          )}
        </div>
      )}

      <div className="tp-panel" style={{ marginTop: 14 }}>
        <h2 className="tp-h2" style={{ fontSize: 18 }}>Payment history</h2>
        {history.length === 0 && <p className="tp-muted" data-testid="hw-pay-history-empty">No payments yet.</p>}
        {history.map((o) => (
          <div className="admin-row" key={o.order_id} style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hw-order-${o.order_id}`}>
            <span className={`status-pill ${o.status === "paid" ? "approved" : o.status === "failed" ? "failed" : "review"}`}>{o.status.toUpperCase()}</span>
            <span style={{ flex: 1, color: "#1e3a5f", fontSize: 13 }}>{o.item_name}<br /><small className="tp-muted">{o.order_id} · {fmtDate(o.created_at)}</small></span>
            <b>{inr(o.amount_paise)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomeworkStore() {
  return <HomeworkShell><PaymentsInner /></HomeworkShell>;
}
